import { SceneKey } from '@models/scene-keys';
import { GameConfigService } from '@services/core/GameConfigService';
import { TranslationService } from '@services/core/TranslationService';
import { LayoutService } from '@services/ui/LayoutService';
import { Button } from '../ui/Button';
import { DPR } from '@constants/common.const';
import { AssetKey } from '@constants/asset-keys.const';
import { ButtonWithIcon } from '../ui/ButtonWithIcon';

export default class Home extends Phaser.Scene {
  constructor() {
    super(SceneKey.Home);
  }

  private layout!: LayoutService;
  private gameLogo!: Phaser.GameObjects.Image;
  private gameSubtitle!: Phaser.GameObjects.Text;
  private playButton!: Button;
  private closeButton!: ButtonWithIcon;
  private configService!: GameConfigService;
  private bgCleanup?: () => void;
  private subtitleTween?: Phaser.Tweens.Tween;
  private playPulseTween?: Phaser.Tweens.Tween;

  create() {
    this.configService = new GameConfigService();
    this.layout = new LayoutService(this.scale);
    const t = new TranslationService();
    // background
    const { cleanup } = this.layout.createHomeBackground(this);
    this.bgCleanup = cleanup;

    const centerX = this.layout.centerX();
    const centerY = this.layout.centerY();

    // Logo sizing: 75% of screen width, max 800px.
    const targetWidth = Math.min(this.layout.screenWidth * 0.75, 800);

    this.gameLogo = this.add
      .image(centerX, centerY - 100 * DPR, AssetKey.GameLogo)
      .setOrigin(0.5);
    const scale = targetWidth / this.gameLogo.width;
    this.gameLogo.setScale(scale);

    const stageName = this.configService.currentStage!.name;
    this.gameSubtitle = this.add
      .text(
        centerX,
        this.gameLogo.y +
          (this.gameLogo.displayHeight ?? this.gameLogo.height) / 2 +
          16 * DPR,
        t.translateArgs('home.subtitle', stageName).toUpperCase(),
        {
          fontFamily: 'korolev, sans-serif',
          fontSize: `${32 * DPR}px`,
          fontStyle: '700',
          align: 'center',
          resolution: DPR,
          wordWrap: {
            width: this.layout.screenWidth * 0.9,
            useAdvancedWrap: true,
          },
        },
      )
      .setOrigin(0.5, 0);

    // Bounce-in subtitle from below
    const subtitleRestY = this.gameSubtitle.y;
    this.gameSubtitle.y = this.layout.screenHeight + this.gameSubtitle.height;
    this.subtitleTween = this.tweens.add({
      targets: this.gameSubtitle,
      y: subtitleRestY,
      duration: 500,
      ease: 'Quad.easeIn',
    });

    this.subtitleTween.on('complete', () => {
      this.playButton = new Button({
        scene: this,
        x: centerX,
        y: subtitleRestY + this.gameSubtitle.height + 52 * DPR,
        text: t.translate('home.playButton'),
        onClick: async () => {
          this.scene.start(SceneKey.Game);
          // this forces the button to stay in the "disabled" state
          // until the Scene is destroyed by Phaser.
          await new Promise(() => {});
        },
      });
      this.playButton.setScale(0.2);

      this.playPulseTween = this.tweens.add({
        targets: this.playButton,
        scale: 1,
        duration: 500,
        ease: 'Sine.easeInOut',
        repeat: 0,
      });
    });

    // add a close button if launched from the hub
    const params = this.configService.sessionParams;
    const hubUrl = import.meta.env.VITE_LOBBY_URL;
    if (params.isFromHub && params.hubGameUuid && hubUrl) {
      this.closeButton = new ButtonWithIcon({
        scene: this,
        x: this.layout.screenWidth - 24 * DPR,
        y: 24 * DPR,
        iconKey: 'close-btn',
        onClick: async () => {
          (window.top || window).location.href =
            `${hubUrl}?token=${params.token}&gameUuid=${params.hubGameUuid}`;
          await new Promise(() => {});
        },
      });
    }

    // Ensure cleanup runs when the scene shuts down
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);

    // Handle dynamic resize
    this.scale.on('resize', this.handleResize, this);
  }

  private handleResize() {
    if (!this.scene?.isActive()) return;
    if (
      !this.gameLogo?.active ||
      !this.gameSubtitle?.active ||
      !this.playButton?.active
    )
      return;

    const centerX = this.layout.centerX();
    const centerY = this.layout.centerY();

    const targetWidth = Math.min(this.layout.screenWidth * 0.75, 800);
    const img = this.gameLogo.texture.getSourceImage() as HTMLImageElement;
    if (!img) return;

    const scale = targetWidth / img.width;

    this.gameLogo.setPosition(centerX, centerY - 100 * DPR);
    this.gameLogo.setScale(scale);

    this.gameSubtitle.setPosition(
      centerX,
      this.gameLogo.y +
        (this.gameLogo.displayHeight ?? this.gameLogo.height) / 2 +
        16 * DPR,
    );

    this.gameSubtitle.setWordWrapWidth(this.layout.screenWidth * 0.9);

    this.playButton.setPosition(
      centerX,
      this.gameSubtitle.y + this.gameSubtitle.height + 52 * DPR,
    );

    // Keep the subtitle tween target in sync if resize happens mid-animation
    if (this.subtitleTween?.isPlaying()) {
      const subtitleRestY =
        this.gameLogo.y +
        (this.gameLogo.displayHeight ?? this.gameLogo.height) / 2 +
        16 * DPR;
      this.subtitleTween.updateTo('y', subtitleRestY, true);
    }

    if (this.closeButton?.active) {
      this.closeButton.setPosition(
        this.layout.screenWidth - 24 * DPR,
        24 * DPR,
      );
    }
  }

  shutdown() {
    // Remove resize listener
    this.scale.off('resize', this.handleResize, this);

    if (this.subtitleTween) {
      this.subtitleTween.stop();
      this.subtitleTween = undefined;
    }

    if (this.playPulseTween) {
      this.playPulseTween.stop();
      this.playPulseTween = undefined;
    }

    // Clean up background resize listener
    if (this.bgCleanup) {
      this.bgCleanup();
    }
  }
}
